import { MinusCircleOutlined } from '@ant-design/icons';
import { DayReminders, getValidators, Thought } from '@cc/schema';
import { Create } from '@refinedev/antd';

import {
  Card,
  Checkbox,
  Divider,
  Form,
  Input,
  Result,
  Table,
  Typography,
} from 'antd';

import {
  IResourceComponentsProps,
  useCustomMutation,
  useGetIdentity,
  useTranslate,
} from '@refinedev/core';

import * as Sentry from '@sentry/react';
import { useActor } from '@xstate/react';
import { RuleObject } from 'antd/lib/form';
import { useWatch } from 'antd/lib/form/Form';
import { set } from 'date-fns';
import qs from 'qs';
import { useContext, useEffect, useMemo, useState } from 'react';
import ReactCardFlip from 'react-card-flip';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { useWindowSize } from 'rooks';
import { TherapistActivityReactContext } from '../../App';
import { DateFnsTimePicker } from '../../components/Pickers';
import { EsCardReminders } from '../../components/EsCardReminders';
import { withLegacyDaySlotsSilenced } from '../../components/EsCardReminders/legacyDaySlots';
import { useClientEra } from '../../hooks/useClientEra';
import { SelectClient } from '../../components/SelectClient';
import { getYupSyncValidator } from '../../helpers';
import { useFormSubmitDisabledAnyTouched } from '../../hooks';
import { useActivityTracking } from '../../hooks/use-activity-tracking';
import { TherapistActivityService } from '../../machines/therapistActivityMachine';
import { IUser } from '../../providers';

const { Text } = Typography;

export const emptyBasicThought = {
  thought: '',
  thoughtBelief: 50,
};

/**
 * E01 · CreatePortalCard — board cc-tools-1, chapter "Thought Testing — Therapist Records Thought
 * (portal, E01)", slice "E01 · CreatePortalCard (portal)" (cells AC1 screen → AC2 command → AC3 /
 * AD3 the two events). This screen is the command's only trigger.
 *
 * Coexistence needs NOTHING here: `createPortalCard` keeps one name for both eras and the SERVER
 * routes by the client's mode (founder 2026-08-31 — the name follows the contract, not the era),
 * so this page has no era branch, no mismatch handling and no stale-tab hazard.
 */
/** The card's ES boundary + its legacy identity — minted by the originator, here the portal. */
const newThoughtTestId = () =>
  `thoughtTests/${
    typeof crypto !== 'undefined' && 'randomUUID' in crypto
      ? crypto.randomUUID()
      : `${Date.now()}-${Math.random().toString(16).slice(2)}`
  }`;

type CreateCopingCardForm = {
  // Thought
  automaticThought: string;
  realisticThoughts: (typeof emptyBasicThought)[];
  // Reminders
  dayReminders?: DayReminders;
};

export const CreateCopingCard: React.FC<IResourceComponentsProps> = () => {
  const [form] = Form.useForm<CreateCopingCardForm>();

  const { t, i18n } = useTranslation();
  const { data: user } = useGetIdentity<IUser>();
  const location = useLocation();
  const translate = useTranslate();

  const { portalDysThoughtTestValidator } = useMemo(
    () => getValidators(translate),
    [i18n.languages[0]],
  );

  const therapistActivityService = useContext(TherapistActivityReactContext);
  const [therapistActivityState, therapistActivitySend] = useActor(
    therapistActivityService as TherapistActivityService,
  );

  const validationRules = getYupSyncValidator(
    translate,
    portalDysThoughtTestValidator,
  ) as RuleObject;

  const [isCardFlipped, setIsCardFlipped] = useState(false);

  // Note: as RuleObject is silenciing Antd since it seems to have wrong types

  const {
    handleFormChange,
    isDisabled: isSubmitDisabled,
    fieldsCount,
  } = useFormSubmitDisabledAnyTouched(form);

  // (P011) this page read the client roster and never looked at it — the only client picker here is
  // `SelectClient`, which reads the shared roster itself. The dead read is gone.

  const {
    mutateAsync,
    data: createData,
    isLoading: createLoading,
    error: createError,
  } = useCustomMutation<{ thought: Thought }>();

  const dayReminders = useWatch('dayReminders', form);

  const dayRemindersColumns = useMemo(() => {
    return [
      {
        title: '',
        dataIndex: 'period',
        render: (period: string) => {
          return <Text strong>{translate(`createThought.${period}`)}</Text>;
        },
      },
      {
        title: '',
        dataIndex: 'time',
        render: (
          time: DayReminders['day']['time'],
          record: DayReminders['day'],
        ) => {
          return (
            <DateFnsTimePicker
              data-testid={`cc-create-coping-card-reminder`}
              style={{
                minWidth: i18n.languages[0]?.startsWith('ru') ? 60 : 100,
              }}
              defaultValue={set(new Date(), {
                hours: time.hours,
                minutes: time.minutes,
              })}
              format={i18n.languages[0]?.startsWith('ru') ? 'HH:mm' : 'hh:mm a'}
              onChange={(date: Date | Date[]) => {
                if (Array.isArray(date)) return;
                const hours = date?.getHours() || 0;
                const minutes = date?.getMinutes() || 0;

                const copiedDayReminders = {
                  ...form.getFieldValue('dayReminders'),
                };
                copiedDayReminders[
                  // @ts-ignore
                  record.key
                ].time = {
                  hours,
                  minutes,
                };
                form.setFieldValue('dayReminders', copiedDayReminders);
              }}
            />
          );
        },
      },
      {
        title: '',
        dataIndex: 'enabled',
        render: (enabled: boolean, record: DayReminders['day']) => {
          return (
            <Checkbox
              checked={enabled}
              onChange={(e) => {
                const checked = e.target.checked;

                const copiedDayReminders = {
                  ...form.getFieldValue('dayReminders'),
                };
                copiedDayReminders[
                  // @ts-ignore
                  record.key
                ].enabled = checked;
                form.setFieldValue('dayReminders', copiedDayReminders);
              }}
            />
          );
        },
      },
    ];
  }, [dayReminders]);

  const dayRemindersData = useMemo(() => {
    return Object.keys(dayReminders || {}).map((period) => {
      return {
        key: period,
        period,
        // @ts-ignore
        ...(dayReminders?.[period] || {}),
      };
    });
  }, [dayReminders]);

  const parsedSearch = qs.parse(
    location.search.charAt(0) === '?'
      ? location.search.slice(1)
      : location.search,
  );

  const [clientId, setClientId] = useState<string | undefined>(
    // E57 (Q-e57g1-2 A rule 4): a create page opened with ?clientId= (Create on a client's tool tab, Use a tool) keeps
    // THAT client; the header bar's filter is the fallback
    (parsedSearch.clientId as string) ||
      therapistActivityState.context.filterClientId ||
      '',
  );

  /**
   * The CLIENT's era for reminders — never the therapist's (plan 07 C3). A converted client gets the
   * RRULE surface; everyone else keeps the shipped 3-slot form, unchanged.
   */
  const { isEsClient } = useClientEra(clientId, 'reminders');

  const { innerWidth } = useWindowSize();
  const textAlign = (innerWidth || 1000) < 995 ? 'center' : 'left';

  const initialValues = useMemo(() => {
    return {
      realisticThoughts: [{ ...emptyBasicThought }],
      dayReminders: {
        morning: {
          time: { hours: 9, minutes: 0 },
          enabled: true,
        },
        day: {
          time: { hours: 14, minutes: 0 },
          enabled: true,
        },
        evening: {
          time: { hours: 21, minutes: 0 },
          enabled: true,
        },
      },
    };
  }, []);

  useEffect(() => {
    if (
      clientId &&
      therapistActivityState.context.filterClientId !== clientId
    ) {
      therapistActivitySend({
        type: 'ENTITY_CLIENT_ID_CHANGED',
        clientId,
      });
    }
  }, [clientId]);

  const onSave = async () => {
    try {
      // ONE operation for both eras — the server routes by the CLIENT's mode (founder 2026-08-31).
      // `thoughtTestId` is the card's ES boundary handle, minted here so a retry is idempotent;
      // the legacy branch ignores it.
      await mutateAsync({
        url: '',
        method: 'post',
        values: {},
        meta: {
          operation: 'createPortalCard',
          fields: [{ thought: ['_id, updatedAt'] }],
          variables: {
            input: {
              name: 'input',
              type: 'PortalCardCreateInput',
              value: {
                /**
                 * `true` = EVERY value in the store, not just the fields that happen to be
                 * MOUNTED. antd's no-arg `getFieldsValue()` returns registered fields only, so
                 * conditionally rendering any field silently drops it from the payload — which is
                 * how hiding the reminder section made `createPortalCard` fail on a required
                 * input (7707c531). With `true` the payload no longer depends on what is on screen.
                 */
                ...form.getFieldsValue(true),
                // …and an ES client's legacy slots go in SILENT: their reminders are the ones they
                // actually set, in the log, through the RRULE surface. See `legacyDaySlots`.
                ...(isEsClient
                  ? {
                      dayReminders: withLegacyDaySlotsSilenced(
                        form.getFieldValue('dayReminders'),
                      ),
                    }
                  : {}),
                clientId,
                thoughtTestId: newThoughtTestId(),
              },
            },
          },
        },
      });
    } catch (e) {
      console.log(e);
      Sentry.captureException(e);
    }
  };

  useEffect(() => {
    if (createData?.data?.thought?.updatedAt) {
      const handle1 = setTimeout(() => {
        setIsCardFlipped(true);
      }, 1500);

      const handle2 = setTimeout(() => {
        setIsCardFlipped(false);
      }, 4500);

      return () => {
        clearTimeout(handle1);
        clearTimeout(handle2);
      };
    }
  }, [createData?.data?.thought?.updatedAt]);

  useEffect(() => {
    if (createData?.data?.thought?._id) {
      const resource =
        t('pages.clientsShow.therapistOtherResources.portalCards') +
        '/' +
        (createData?.data?.thought._id.split('/')[1] || '');
      therapistActivitySend({
        type: 'ENTITY_CLIENT_ID_CHANGED',
        clientId,
      });
      therapistActivitySend({
        type: 'CREATED',
        resource,
      });
    }
  }, [createData?.data?.thought?._id]);

  useActivityTracking(
    createData?.data?.thought?._id
      ? t('pages.clientsShow.therapistOtherResources.portalCards') +
          '/' +
          (createData?.data?.thought._id.split('/')[1] || '/create')
      : `${t('pages.clientsShow.therapistOtherResources.portalCards')}/create`,
  );

  return (
    <Create
      title={translate('pages.copingCards.create')}
      saveButtonProps={{
        onClick: onSave,
        disabled:
          isSubmitDisabled ||
          !!(fieldsCount < 2) ||
          !!createData?.data?.thought._id,
      }}
    >
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          marginBottom: '30px',
        }}
      >
        <SelectClient clientId={clientId} setClientId={setClientId} />
      </div>
      {clientId && (
        <Form<CreateCopingCardForm>
          data-testid="cc-create-card"
          form={form}
          initialValues={initialValues}
          onFinish={(values) => {
            console.log('values', values);
          }}
          onFieldsChange={handleFormChange}
          labelCol={{ span: 5 }}
          wrapperCol={{ span: 10 }}
          labelWrap
        >
          <Form.Item
            label={translate('pages.copingCards.frontSide')}
            name="automaticThought"
            required
            rules={[validationRules]}
            tooltip={translate('pages.copingCards.frontSideTip')}
          >
            <Input.TextArea
              data-testid="cc-create-card-front-side"
              autoSize={{ minRows: 4, maxRows: 8 }}
              placeholder={translate('pages.copingCards.frontSide')}
            />
          </Form.Item>

          <Form.List
            name="realisticThoughts"
            // @ts-ignore
            rules={[validationRules]}
          >
            {(fields, { add, remove }, errors) => {
              return (
                <>
                  {fields.map((field, index) => {
                    return (
                      <Form.Item
                        label={
                          index === 0
                            ? translate('pages.copingCards.backSide')
                            : ''
                        }
                        wrapperCol={{
                          offset: index === 0 ? 0 : 5,
                          span: 10,
                        }}
                        key={field.key}
                        required={index === 0}
                        tooltip={translate('pages.copingCards.backSideTip')}
                      >
                        <div style={{ display: 'flex' }}>
                          <Form.Item
                            {...field}
                            style={{ width: '100%' }}
                            name={[field.key, 'thought']}
                            rules={[
                              {
                                required: true,
                                message: translate(
                                  'pages.copingCards.backSideRequired',
                                ),
                              },
                            ]}
                          >
                            <Input.TextArea
                              data-testid="cc-create-card-back-side"
                              autoSize={{ minRows: 4, maxRows: 8 }}
                              placeholder={translate(
                                'pages.copingCards.backSideTip',
                              )}
                            />
                          </Form.Item>
                          {fields.length > 1 ? (
                            <MinusCircleOutlined
                              data-testid="cc-create-card-remove-back-side"
                              style={{ margin: '5px 0 0 10px' }}
                              onClick={() => remove(field.name)}
                            />
                          ) : null}
                        </div>
                      </Form.Item>
                    );
                  })}
                </>
              );
            }}
          </Form.List>

          {/*
            THE REMINDER SECTION IS REPLACED IN PLACE, NOT REMOVED (founder, 2026-09-10) — the same
            rule as section 11 of the Thought Testing form, and the rule mobile follows: it swaps
            the whole screen behind an `EraGate` (legacy `CardNotificationsScreen` / ES
            `CardNotificationsEsScreen`) so a person always finds reminders where reminders live,
            and only the contents differ. Hiding the section for an ES client took the affordance
            away instead of upgrading it.

            WHY THE CONTENTS MUST DIFFER. The 3-slot table can express only "a time, on/off" for
            three fixed slots, and for an ES client it does not fail — `updateDayReminders` routes
            into the log via `applyDayRemindersEs`. It rewrites whatever rule the client had to
            `FREQ=DAILY`, so a therapist adjusting the time on a weekly reminder would silently
            destroy the weekly rule.

            WHY IT WAITS FOR THE SAVE. A schedule points AT a card, so there is nothing to point at
            until the card exists. Mobile never meets this because it opens reminders from a card
            that already exists; a create form does. The section stays either way, and says so.
          */}
          {/*
            `dayReminders` IS REQUIRED BY THE MUTATION — `DayRemindersCreateInputNoThught!` — so the
            field stays MOUNTED in both eras and only its UI changes. Unmounting it unregisters it,
            antd drops it from `getFieldsValue()`, and `createPortalCard` is rejected with
            BAD_USER_INPUT: "Field dayReminders of required type … was not provided".

            That is not hypothetical: hiding this section for ES clients (commit 7707c531, deployed
            2026-09-10) broke card creation for exactly them. The E01 spec passed anyway because
            Playwright fills and saves in about a second and `useClientEra` had not resolved yet, so
            the legacy table was still mounted — a human, who reads the form first, would always
            lose that race. The spec now waits for the era before saving, so it can never pass by
            beating the hook again.
          */}
          <Form.Item label={translate('createThought.timeRemindersTitle')}>
            <Form.Item
              name="dayReminders"
              rules={[validationRules]}
              noStyle
              // registered, and carrying the defaults, even while the RRULE surface is what shows
              hidden={isEsClient}
            >
              <Table
                data-testid="cc-create-card-reminders"
                showHeader={false}
                columns={dayRemindersColumns}
                dataSource={dayRemindersData}
                pagination={false}
              />
            </Form.Item>
            {isEsClient &&
              (createData?.data?.thought?._id ? (
                <EsCardReminders
                  subject={clientId}
                  targetRef={createData.data.thought._id}
                />
              ) : (
                <Text
                  type="secondary"
                  data-testid="cc-create-card-es-reminders-hint"
                >
                  {translate('esReminders.afterSave')}
                </Text>
              ))}
          </Form.Item>

          {!createLoading &&
            (createData?.data?.thought?.updatedAt || !!createError) && (
              <Result
                data-testid="cc-create-card-result"
                status={createError ? 'error' : 'success'}
                title={
                  createError
                    ? createError.message
                    : translate('pages.copingCards.createSuccess')
                }
                extra={
                  <div
                    style={{
                      display: 'flex',
                      width: '100%',
                      justifyContent: 'center',
                      flexDirection: 'column',
                      alignItems: 'center',
                    }}
                  >
                    {/*
                      Reminders are NOT repeated here — they live in the reminder section of the
                      form above, which is where a reminders section belongs and where it stays in
                      both eras. The save unlocks that section rather than relocating it.
                    */}
                    <div style={{ width: 400 }}>
                      <ReactCardFlip
                        isFlipped={isCardFlipped}
                        flipDirection="horizontal"
                        flipSpeedBackToFront={2}
                        flipSpeedFrontToBack={2}
                        data-testid="cc-create-card-flip"
                      >
                        <Card
                          data-testid="cc-create-card-flip-front"
                          style={{ width: 400 }}
                          onClick={() => setIsCardFlipped(!isCardFlipped)}
                          bordered
                          hoverable
                          bodyStyle={{
                            minHeight: 200,
                            boxShadow: '0 0 10px #ccc',
                            background: '#45403e',
                            color: '#f5f1f1',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            borderRadius: 10,
                          }}
                        >
                          {form.getFieldValue('automaticThought')}
                        </Card>

                        <Card
                          data-testid="cc-create-card-flip-back"
                          style={{ width: 400 }}
                          onClick={() => setIsCardFlipped(!isCardFlipped)}
                          bordered
                          hoverable
                          bodyStyle={{
                            minHeight: 200,
                            boxShadow: '0 0 10px #ccc',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            borderRadius: 10,
                          }}
                        >
                          {form
                            .getFieldValue('realisticThoughts')
                            ?.map((t: any, idx: number) => {
                              return <p key={idx}>{t.thought}</p>;
                            })}
                        </Card>
                      </ReactCardFlip>
                    </div>
                  </div>
                }
              />
            )}
          <Divider />
        </Form>
      )}
    </Create>
  );
};
